import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import _ from 'lodash';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { Badge, BadgeProps, Button, CircularProgress, Stack, Typography, Box, Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ActionMenu, QuickSearch } from 'components/_atoms';
import { translate as t } from 'hooks/useTranslations';
import datetimeHelper from 'helpers/datetimeHelper';
import useUrlParams from 'hooks/useUrlParams';
import { AppContext } from 'views/App/context';
import useManageDTBPackages from '../_data_source/useManageDTBPackage';
import useDTBPackages from '../_data_source/useDTBPackages';
import DTBPackageFilters from '../_components/DTBPackageFilters';
import { DTBPackage, GetDTBPackageParams } from 'views/App/Portal/DTB/_types/dtb';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import GridSection from 'components/_layouts/GridSection';
import PageToolbar from '_lex/_atoms/PageToolbar';
import PhxBadge from '_lex/_atoms/PhxBadge';
import DtbLevelFilter from '../../_components/DtbLevelFilter';
import JurisdictionFilter from '../../_components/JurisdictionFilter';
import dayjs from 'dayjs';
import { useConfirm } from 'material-ui-confirm';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import useExportDTBPackage from '../_data_source/useExportDTBPackage';
import { downloadFileFromBase64, downloadFileFromUrl } from 'views/App/Portal/Policy/DraftManuals/helpers/exportHelper';
import errorHelper from 'helpers/errorHelper';

const defaultParams: GetDTBPackageParams = {
  orgId: '',
  limit: undefined,
  nextToken: undefined,
  distributableChanges: undefined,
  jurisdictionId: undefined,
  level: undefined,
  status: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  search: undefined,
  manualId: undefined,
};

const DTBPackageList: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganizationId, isLexipolStaff } = useContext(AppContext);
  const queryClient = useQueryClient();

  const [params, setParams] = useUrlParams(_.omit(defaultParams, ['orgId']));
  const [packages, setPackages] = useState<DTBPackage[]>([]);
  const { remove, approvePackage } = useManageDTBPackages();
  const [loadingActionId, setLoadingActionId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const confirm = useConfirm();


  const queryParams: GetDTBPackageParams = useMemo(() => {
    const cleanParams = _.omitBy({ ...params }, _.isEmpty);
    return {
      orgId: currentOrganizationId,
      limit: cleanParams.limit,
      jurisdictionId: cleanParams.jurisdictionId,
      level: cleanParams.level,
      status: cleanParams.status,
      createdAtStart: cleanParams.createdAtStart
        ? dayjs(cleanParams.createdAtStart).startOf('day').toISOString()
        : undefined,
      createdAtEnd: cleanParams.createdAtEnd ? dayjs(cleanParams.createdAtEnd).endOf('day').toISOString() : undefined,
      search: cleanParams.search,
      manualId: cleanParams.manualId,
    };
  }, [currentOrganizationId, params]);

  const packagesQuery = useDTBPackages(queryParams);
  const { exportDTBPackage } = useExportDTBPackage();

  useEffect(() => {
    if (packagesQuery.data && !packagesQuery.isError) {
      const fetchedPackages = packagesQuery.data.pages.flatMap((page: { content: DTBPackage[] }) => page.content) || [];
      setPackages(fetchedPackages);
    }
  }, [packagesQuery.data, packagesQuery.isError]);

  const updateParams = useCallback(
    (updatedParams: GetDTBPackageParams) => {
      setParams((prev: GetDTBPackageParams) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams({ ..._.omit(defaultParams, ['orgId']) });
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const onSearchChange = useCallback(
    (val: string) => {
      updateParams({ search: val } as GetDTBPackageParams);
    },
    [updateParams, params]
  );

  const isFiltered = useMemo(() => !_.isEmpty(_.omitBy(params, _.isEmpty)), [params]);

  const handleEditClick = (row: DTBPackage) => {
    navigate(`./${row.id}`);
  };

  const handleDeleteClick = (row: DTBPackage) => {
    confirm({
      title: t('dtbs.packages.delete.confirmTitle', 'Delete Package'),
      description: t('dtbs.packages.delete.confirmBody'),
      confirmationText: t('dtbs.packages.delete.confirmationText'),
      cancellationText: t('dtbs.packages.delete.cancellationText'),
      confirmationButtonProps: { variant: 'contained', color: 'error' },
      cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
    })
      .then(async () => {
        setLoadingActionId(row.id);
        try {
          await remove(currentOrganizationId, row.id);
          enqueueSnackbar(t('dtbs.packages.delete.success'), { variant: 'success' });
          // Refetch the list
          packagesQuery.refetch();
        } catch (error) {
          enqueueSnackbar(t('dtbs.packages.delete.error'), { variant: 'error' });
        } finally {
          setLoadingActionId(null);
        }
      })
      .catch(() => {});
  };

  const handleApproveClick = (row: DTBPackage) => {
    confirm({
      title: t('dtbs.packages.approveConfirmTitle', 'Approve Package'),
      description: t('dtbs.packages.approveConfirmBody', 'Are you sure you want to approve this package?'),
      confirmationText: t('dtbs.packages.approveConfirm', 'Approve'),
      cancellationText: t('common.cancel', 'Cancel'),
      confirmationButtonProps: { variant: 'contained', color: 'success' },
      cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
    })
      .then(async () => {
        setApprovingId(row.id);
        try {
          await approvePackage(currentOrganizationId, row.id);
          enqueueSnackbar(t('dtbs.packages.approveSuccess', 'Package approved successfully.'), { variant: 'success' });
          // Invalidate both queries
          queryClient.invalidateQueries(['dtbPackages']);
          queryClient.invalidateQueries(['assignmentPackages']);
          queryClient.invalidateQueries(['distributionPackages']);
        } catch (error) {
          enqueueSnackbar(t('dtbs.packages.approveError', 'Failed to approve package.'), { variant: 'error' });
        } finally {
          setApprovingId(null);
        }
      })
      .catch(() => {});
  };

  const handleExportClick = async (row: any) => {
    if (exportingId) return;
    setExportingId(row.id);
    let fileName = row.name;

    try {
      await exportDTBPackage(
        currentOrganizationId,
        row.id,
        async (response) => {
          try {
            if (response.base64) {
              // Direct base64 response (used for single content exports)
              downloadFileFromBase64({
                base64Content: response.base64,
                fileName,
                fileType: response.fileType || 'PDF',
              });
            } else if (response.presignedUrl) {
              // Presigned URL response (used for full manual exports)
              await downloadFileFromUrl({
                presignedUrl: response.presignedUrl,
                fileName,
                fileType: 'PDF',
                onProgress: (attempt, maxAttempts) => {
                  //setExportProgress({ attempt, maxAttempts });
                },
              });
            } else {
              throw new Error('Invalid export response format');
            }

            enqueueSnackbar(t('dtbs.packages.export.exportSuccess'), { variant: 'success' });
          } catch (downloadError) {
            const errorMessage = 
              downloadError instanceof Error && downloadError.message?.includes('Time out has been exceeded')
                ? t('dtbs.export.timeoutError')
                : t('dtbs.export.downloadError');
            enqueueSnackbar(errorMessage, { variant: 'error' });
          }
        },
        (error) => {
          console.error('Export error:', error);
          errorHelper.enqueueErrors(error);
        }
      );
    } catch (error) {
      // Catch any unhandled errors to prevent React error boundary from showing
      console.error('Unhandled export error:', error);
      errorHelper.enqueueErrors(error as any);
    } 
    finally {
      setExportingId(null);
    }
  };

  const columns = useMemo(
    () => ({
      packageName: {
        header: t('dtbs.packages.table.title'),
        flex: 3,
        onClick: (params: GridCellParams) => handleEditClick(params.row as DTBPackage),
        renderer: (params: GridCellParams) => <Typography variant='h6'>{params.row.name}</Typography>,
      },
      dtbs: {
        header: t('dtbs.packages.table.dtbs'),
        flex: 1,
        renderer: (params: GridCellParams) => <Typography>{params.row.dtbs?.length || 0}</Typography>,
      },
      manual: {
        header: t('dtbs.packages.table.manual'),
        flex: 1,
        renderer: (params: GridCellParams) => <Typography>{params.row.manual?.name || ''}</Typography>,
      },
      createdAt: {
        header: t('dtbs.packages.table.createdAt'),
        flex: 1,
        renderer: (params: GridCellParams) => (
          <Typography>{datetimeHelper.formatDate(params.row.createdAt)}</Typography>
        ),
      },
      status: {
        header: t('dtbs.packages.table.status'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const status = (params.row.status || 'DRAFT').toUpperCase();
          const color = status === 'APPROVED' ? 'success' : 'neutral';
          return <PhxBadge color={color}>{t(`dtbs.status.${status}`, status)}</PhxBadge>;
        },
      },
      actions: {
        header: '',
        type: 'actions',
        flex: 0.3,
        renderer: (params: GridCellParams) => {
          const row = params.row as DTBPackage;
          if (loadingActionId === row.id) {
            return (
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
              >
                <CircularProgress size={20} />
              </Stack>
            );
          }
          if (approvingId === row.id) {
            return (
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
              >
                <CircularProgress size={20} />
              </Stack>
            );
          }
          if (exportingId === row.id) {
            return (
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
              >
                <CircularProgress size={20} />
              </Stack>
            );
          }
          const items = [
            {
              key: `edit_${row.id}`,
              label: t('dtbs.packages.table.managePackage'),
              onClick: () => handleEditClick(row),
            },
            {
              key: `edit_properties_${row.id}`,
              label: t('dtbs.packages.table.editProperties', 'Edit Properties'),
              onClick: () => navigate(`./${row.id}/edit-properties`),
            },
            {
              key: `delete_${row.id}`,
              label: t('dtbs.packages.table.delete', 'Delete'),
              onClick: () => handleDeleteClick(row),
              disabled: !!loadingActionId,
            },
          ];
          if ((row.status || '').toUpperCase() !== 'APPROVED' && row.dtbs?.length > 0) {
            items.push({
              key: `approve_${row.id}`,
              label: t('dtbs.packages.table.approve', 'Approve'),
              onClick: () => handleApproveClick(row),
            });
          }
          items.push({
            key: `clone_${row.id}`,
            label: t('dtbs.packages.table.clone', 'Clone'),
            onClick: () => navigate(`./${row.id}/clone`),
          });
          items.push({
            key: `export_${row.id}`,
            label: t('dtbs.packages.table.export', 'Export'),
            onClick: () => handleExportClick(params.row),
            disabled: !!exportingId,
          });
          return (
            <ActionMenu
              id={`action_menu_${row.id}`}
              items={items}
            />
          );
        },
      },
    }),
    [t, navigate, loadingActionId, approvingId, exportingId]
  );

  const onHandleCreatePackage = () => {
    navigate(`./new`);
  };

  const gridViewQuery = useMemo(
    () => ({
      hasNextPage: packagesQuery.hasNextPage,
      isFetchingNextPage: packagesQuery.isFetchingNextPage,
      fetchNextPage: packagesQuery.fetchNextPage,
      isError: packagesQuery.isError,
      isLoading: packagesQuery.isLoading,
      error: packagesQuery.error,
      data: {
        pages:
          packagesQuery.data?.pages.map((page: { content: DTBPackage[]; count: number }) => ({
            count: page.count,
            rows: page.content,
          })) || [],
      },
    }),
    [packagesQuery]
  );

  const onLevelChange = useCallback(
    (value: (string | null)[]) => {
      setParams((prev: GetDTBPackageParams) => ({
        ...prev,
        level: value[0] || undefined,
      }));
    },
    [setParams]
  );

  const onJurisdictionChange = useCallback(
    (value: (string | null)[]) => {
      setParams((prev: GetDTBPackageParams) => ({
        ...prev,
        jurisdictionId: value[0] || undefined,
      }));
    },
    [setParams]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('dtbs.packages.title'),
        subtitle: t('dtbs.packages.subTitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <Stack
              direction='row'
              spacing={3}
              sx={{
                alignItems: 'center',
                '& > *': {
                  minWidth: 'fit-content',
                  flexShrink: 0,
                },
              }}
            >
              <QuickSearch
                id='keyword_search'
                initialValue={(params as any).search || ''}
                onChange={onSearchChange}
                placeholder={t('dtbs.packages.searchPlaceholder')}
              />
              {isLexipolStaff && (
                <>
                  <Box sx={{ width: '200px' }}>
                    <DtbLevelFilter
                      id='level-picker'
                      value={params.level ? [params.level] : []}
                      onChange={onLevelChange}
                      placeholder={t('dtbs.library.level.ALL')}
                      showAllOption
                      lexipolOnly
                    />
                  </Box>
                  <Box sx={{ width: '200px' }}>
                    <JurisdictionFilter
                      id='jurisdiction-picker'
                      value={params.jurisdictionId ? [params.jurisdictionId] : []}
                      onChange={onJurisdictionChange}
                      showAllOption
                      size='small'
                      placeholder={t('dtbs.library.filters.allJurisdictions')}
                    />
                  </Box>
                </>
              )}
              <DTBPackageFilters
                params={params}
                updateParams={updateParams}
              />
              {isFiltered && (
                <Button
                  color='neutral'
                  onClick={clearParams}
                  size='small'
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: '80px',
                    px: 2,
                  }}
                >
                  {t('dtbs.packages.clear')}
                </Button>
              )}
            </Stack>
          }
          rightItems={
            <Stack
              direction='row'
              spacing={2}
              sx={{ alignItems: 'center', display: 'flex' }}
            >
              <Button
                size='small'
                onClick={onHandleCreatePackage}
              >
                {t('dtbs.packages.createPackage')}
              </Button>
            </Stack>
          }
        />
        {packagesQuery.isLoading ? (
          <Card sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Card>
        ) : packages.length === 0 ? (
          <Card sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Stack
              spacing={3}
              alignItems='center'
            >
              <Box
                component='img'
                src='/admin/img/policy/dtbs/empty-package-placeholder.png'
                alt='Empty packages'
              />
              <Typography
                variant='subtitleLg'
                color='text.secondary'
              >
                {t(isFiltered ? 'dtbs.packages.emptyStateFiltered' : 'dtbs.packages.emptyState')}
              </Typography>
            </Stack>
          </Card>
        ) : (
          <GridSection
            columns={columns}
            data={packages}
            query={gridViewQuery}
            gridProps={{
              autosizeOptions: {
                columns: ['createdAt', 'jurisdiction', 'vertical', 'status'],
                includeOutliers: true,
                includeHeaders: true,
              },
            }}
          />
        )}
      </Box>
    </CommonLayout>
  );
};

export default DTBPackageList;
